import React, { useState } from 'react';
import styled from 'styled-components';
import { TextField, IconButton, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { sendMessage } from '../../services/chatService';
import { translations } from './config/languages';

const InputContainer = styled.div`
  display: flex;
  padding: 12px 16px;
  gap: 8px;
  background-color: white;
  border-top: 1px solid #eee;
  align-items: flex-end;
`;

const StyledTextField = styled(TextField)`
  && {
    flex: 1;
    
    .MuiOutlinedInput-root {
      border-radius: 20px;
      background-color: #f8f9fa;
      
      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: #2e3092;
      }
      
      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: #2e3092;
      }
    }
  }
`;

const ActionButton = styled(IconButton)`
  && {
    color: #2e3092;
    padding: 8px;
    
    &:hover {
      background-color: rgba(46, 48, 146, 0.1);
    }
    
    &.Mui-disabled {
      color: rgba(0, 0, 0, 0.26);
    }
  }
`;

const ChatInput = ({ 
  onSendMessage, 
  onBotResponse, 
  allowImageUpload = false,
  allowMultilineInput = false,
  language = 'en'
}) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const t = translations[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      setIsLoading(true);
      const userMessage = message.trim();
      setMessage('');
      onSendMessage(userMessage);

      try {
        const response = await sendMessage(userMessage);
        onBotResponse(response.message);
      } catch (error) {
        console.error('Failed to get response:', error);
        onBotResponse(t.errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (!allowMultilineInput && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <InputContainer>
        {allowImageUpload && (
          <ActionButton size="small">
            <AttachFileIcon />
          </ActionButton>
        )}
        <StyledTextField
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t.inputPlaceholder}
          variant="outlined"
          size="small"
          disabled={isLoading}
          multiline={allowMultilineInput}
          maxRows={allowMultilineInput ? 4 : 1}
          rows={1}
        />
        <ActionButton 
          type="submit" 
          disabled={!message.trim() || isLoading}
          size="small"
        >
          {isLoading ? <CircularProgress size={20} /> : <SendIcon />}
        </ActionButton>
      </InputContainer>
    </form>
  );
};

export default ChatInput; 